import {
  generateRefTag,
  type PLATFORMS,
  type ProductLines,
  type ReasonNaming,
  RefTagProductLineMapping,
  RefTagSlotMapping,
} from '@amzn/journeys-common';
import { type LinkTypeOptionsValue, linkTypeOptions } from 'app.constants';
import { FormLabelWithOptional } from 'components/form/FormLabelWithOptional';
import { JFormSelect } from 'components/form/JFormSelect';
import { MFormToggle } from 'components/MeridianFinalFormWrappers/MFormToggle';
import type { DestinationUrl } from 'generated/graphql';
import { type FC, useCallback, useEffect } from 'react';
import { Form } from 'react-bootstrap';
import { useField, useForm } from 'react-final-form';
import { useEffectOnce } from 'react-use';
import { getUpdatedDestinationUrl, required } from '../../../components/form/form.util';
import { JFormInput } from '../../../components/form/JFormInput';
import type { CampaignFormValues, DestinationUrlInput } from '../campaign.types';
import { asinValidator, browseNodeValidator, dealsValidator, urlValidator } from '../campaign.util';

export type CampaignContentDestinationUrlProps = {
  label: string;
  fieldPath: string;
};

export const CampaignContentDestinationUrl: FC<CampaignContentDestinationUrlProps> = ({ label, fieldPath }) => {
  const form = useForm<CampaignFormValues>();
  const campaign = form.getState().values.campaign;
  const field = useField(fieldPath);
  const destinationUrl = field.input.value as DestinationUrl | undefined;
  const refTag = destinationUrl?.refTag;
  const refTagFieldPath = `${fieldPath}.refTag`;
  const linkType = destinationUrl?.linkType;
  const disabled = !!campaign.isLocked;

  useEffectOnce(() => {
    if (!destinationUrl) {
      form.change(
        fieldPath as keyof CampaignFormValues,
        {
          generatedUrl: '',
          linkType: 'ASIN',
          linkTypeValue: '',
          refTag: {
            additionalText: '',
            autoGenerate: false,
            eventShortName: '',
            productLine: '',
            programShortName: '',
            slot: '',
            value: '',
          },
        } as unknown as CampaignFormValues[keyof CampaignFormValues],
      );
    }
  });

  // biome-ignore lint/correctness/useExhaustiveDependencies: legacy code
  const linkTypeChanged = useCallback(
    (value: LinkTypeOptionsValue) => {
      if (value === 'Manual') {
        form.change(
          `${fieldPath}.generatedUrl` as keyof CampaignFormValues,
          '/dp/' as unknown as CampaignFormValues[keyof CampaignFormValues],
        );
      }
      form.change(`${fieldPath}.linkTypeValue` as keyof CampaignFormValues, '' as unknown as CampaignFormValues[keyof CampaignFormValues]);
    },
    [
      destinationUrl?.linkType,
    ],
  );

  const linkTypeValueValidate = useCallback(
    (value: string) => {
      if (!destinationUrl) {
        return; // Handle case where destinationUrl is undefined or null
      }
      const { linkType } = destinationUrl;
      switch (linkType) {
        case 'ASIN':
          return asinValidator(value);
        case 'Browse node':
          return browseNodeValidator(value);
        case 'Deals':
          return dealsValidator(value);
        default:
          return; // Handle default case
      }
    },
    [
      destinationUrl,
    ],
  );

  // biome-ignore lint/correctness/useExhaustiveDependencies: legacy code
  const generatedUrlValidate = useCallback(
    (value: string) => urlValidator(value),
    [
      destinationUrl?.generatedUrl,
    ],
  );

  // biome-ignore lint/correctness/useExhaustiveDependencies: legacy code
  const slotValidate = useCallback(
    (value: string, allValues: CampaignFormValues) => {
      if (allValues.campaign.placements[0].slot && allValues.campaign.placements[0].slot in RefTagSlotMapping) {
        return;
      }
      return required(value);
    },
    [
      campaign.placements[0].slot,
    ],
  );

  // biome-ignore lint/correctness/useExhaustiveDependencies: legacy code
  const productLineValidate = useCallback(
    (value: string, allValues: CampaignFormValues) => {
      if (allValues.campaign.productLine && allValues.campaign.productLine in RefTagProductLineMapping) {
        return;
      }
      return required(value);
    },
    [
      campaign.productLine,
    ],
  );

  const updateRefTagValue = (index: number, value: string) => {
    const refTagValueParams = [
      refTag?.eventShortName ?? '',
      refTag?.slot ?? '',
      refTag?.productLine ?? '',
      refTag?.programShortName ?? '',
      refTag?.additionalText ?? '',
    ];
    if (index >= 0) {
      refTagValueParams[index] = value;
    }
    const generatedRefTagValue = generateRefTag({
      additionalText: refTagValueParams[4],
      eventName: refTagValueParams[0],
      fallbackProductLine: refTagValueParams[2],
      fallbackSlot: refTagValueParams[1],
      platform: campaign.placements[0].platform as PLATFORMS,
      productLine: campaign.productLine as ProductLines,
      program: refTagValueParams[3],
      reason: campaign.reason as ReasonNaming,
      slot: campaign.placements[0].slot ?? '',
    });
    form.change(
      `${refTagFieldPath}.value` as string as keyof CampaignFormValues,
      generatedRefTagValue as unknown as CampaignFormValues[keyof CampaignFormValues],
    );
  };

  // update the value field according to 5 fields
  const refTagDependencyList = [
    refTag?.autoGenerate,
    refTag?.eventShortName,
    refTag?.programShortName,
    refTag?.slot,
    refTag?.productLine,
    refTag?.additionalText,
  ];
  const handleChangeAutoGenerate = useCallback((checked: boolean) => {
    // trigger updateRefTagValue(...) only when user enable auto-generate
    if (checked) {
      updateRefTagValue(-1, '');
    }
    // biome-ignore lint/correctness/useExhaustiveDependencies: legacy code
  }, refTagDependencyList);
  const eventShortNameChanged = useCallback((value: string) => {
    updateRefTagValue(0, value);
    // biome-ignore lint/correctness/useExhaustiveDependencies: legacy code
  }, refTagDependencyList);
  const programShortNameChanged = useCallback((value: string) => {
    updateRefTagValue(3, value);
    // biome-ignore lint/correctness/useExhaustiveDependencies: legacy code
  }, refTagDependencyList);
  const slotChanged = useCallback((value: string) => {
    updateRefTagValue(1, value);
    // biome-ignore lint/correctness/useExhaustiveDependencies: legacy code
  }, refTagDependencyList);
  const productLineChanged = useCallback((value: string) => {
    updateRefTagValue(2, value);
    // biome-ignore lint/correctness/useExhaustiveDependencies: legacy code
  }, refTagDependencyList);
  const additionalTextChanged = useCallback((value: string) => {
    updateRefTagValue(4, value);
    // biome-ignore lint/correctness/useExhaustiveDependencies: legacy code
  }, refTagDependencyList);

  // trigger updateRefTagValue(...) when fields changed outside destination url
  // biome-ignore lint/correctness/useExhaustiveDependencies: legacy code
  useEffect(() => {
    if (refTag?.autoGenerate) {
      updateRefTagValue(-1, '');
    }
  }, [
    refTag?.autoGenerate,
    campaign.placements[0].platform,
    campaign.productLine,
    campaign.reason,
    campaign.placements[0].slot,
  ]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: legacy code
  useEffect(() => {
    const updatedDestinationUrl = getUpdatedDestinationUrl({
      asin: destinationUrl?.linkTypeValue ?? null,
      browseNode: destinationUrl?.linkTypeValue ?? null,
      destinationUrl: destinationUrl?.generatedUrl ?? null,
      linkType: (destinationUrl?.linkType as DestinationUrlInput) ?? null,
      platform: campaign.placements[0].platform,
      refTag: refTag?.value ?? null,
    });
    form.change(
      `${fieldPath}.generatedUrl` as string as keyof CampaignFormValues,
      updatedDestinationUrl as unknown as CampaignFormValues[keyof CampaignFormValues],
    );
  }, [
    destinationUrl?.linkType,
    destinationUrl?.linkTypeValue,
    refTag?.autoGenerate,
    refTag?.value,
    campaign.placements[0].platform,
  ]);

  return (
    <div>
      <FormLabelWithOptional label={label} />
      <Form.Group>
        <div className="form-row">
          <JFormSelect
            className="col-3"
            disabled={disabled}
            label="Link Type"
            name={`${fieldPath}.linkType`}
            onChange={linkTypeChanged}
            options={linkTypeOptions}
            textField="display"
            validate={required}
            valueField="value"
          />
          {linkType === 'ASIN' || linkType === 'Browse node' ? (
            <>
              <JFormInput
                className="col-3"
                disabled={disabled}
                label={linkType}
                name={`${fieldPath}.linkTypeValue`}
                type="text"
                validate={linkTypeValueValidate}
              />

              <JFormInput
                className="col-4"
                disabled={disabled || !!refTag?.autoGenerate}
                label="Ref tag"
                name={`${refTagFieldPath}.value`}
                type="text"
                validate={required}
              />
              <Form.Group className="col flex">
                <FormLabelWithOptional label="Auto-generate" />
                <MFormToggle disabled={disabled} name={`${refTagFieldPath}.autoGenerate`} onChange={handleChangeAutoGenerate} />
              </Form.Group>
            </>
          ) : (
            <JFormInput
              className="col-9"
              disabled={disabled}
              label="Link"
              name={`${fieldPath}.generatedUrl`}
              type="text"
              validate={generatedUrlValidate}
            />
          )}
        </div>
        {refTag?.autoGenerate && (
          <div className="auto-naming-options-section">
            <h4 className="text-center">{`ref=${refTag.value}`}</h4>
            <div className="form-row">
              <JFormInput
                className="col"
                description="Example: VDAY"
                disabled={disabled}
                label="Ref tag: Event short name"
                name={`${refTagFieldPath}.eventShortName`}
                onChange={eventShortNameChanged}
                optional
              />
              <JFormInput
                className="col"
                description="Example: SP"
                disabled={disabled}
                label="Ref tag: Program short name"
                name={`${refTagFieldPath}.programShortName`}
                onChange={programShortNameChanged}
                optional
              />
            </div>
            <div className="form-row">
              <JFormInput
                className="col"
                description="This field is only necessary if it cannot be automatically filled based on this campaign's selected slots. Example: H4"
                disabled={disabled}
                label="Ref tag: Slot"
                name={`${refTagFieldPath}.slot`}
                onChange={slotChanged}
                validate={slotValidate}
              />
              <JFormInput
                className="col"
                description="This field is only necessary if it cannot be automatically filled based on this campaign's selected Product Line. Example: ERD"
                disabled={disabled}
                label="Ref tag: Product line"
                name={`${refTagFieldPath}.productLine`}
                onChange={productLineChanged}
                validate={productLineValidate}
              />
            </div>
            <div className="form-row">
              <JFormInput
                className="col"
                description="This text will be appended to the end of the ref tag."
                disabled={disabled}
                label="Ref tag: Additional text"
                name={`${refTagFieldPath}.additionalText`}
                onChange={additionalTextChanged}
                optional
              />
            </div>
          </div>
        )}
      </Form.Group>
    </div>
  );
};
